import {
  TOP_UP_PACKS,
  type TopUpPackId,
  formatCents,
  formatCredits,
} from "@models/billing";
import BoltIcon from "@mui/icons-material/BoltRounded";
import CloseIcon from "@mui/icons-material/CloseRounded";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useStartTopup } from "../hooks/useBilling";

interface TopUpModalProps {
  open: boolean;
  onClose: () => void;
  /** Pre-select a pack — used by the out-of-credits inline card. */
  initialPackId?: TopUpPackId;
}

/**
 * Three-pack chooser. The middle pack is highlighted as recommended (it
 * has the best credits-per-dollar ratio for most users); the bulk pack
 * carries the bigger bonus chip.
 */
export function TopUpModal({ open, onClose, initialPackId }: TopUpModalProps) {
  const [packId, setPackId] = useState<TopUpPackId>(initialPackId ?? "p5_5k");
  const startTopup = useStartTopup();

  const handleConfirm = () => {
    startTopup.mutate({ packId });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: { sx: { bgcolor: "surface.default", borderRadius: 2 } },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Box>
          <Typography variant="overline" sx={{ color: "text.disabled" }}>
            Top up
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            Add credits to your wallet
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          Top-up credits never expire while your subscription is active.
        </Typography>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          {TOP_UP_PACKS.map((pack, idx) => {
            const selected = packId === pack.id;
            const recommended = idx === 1;
            return (
              <Box
                key={pack.id}
                component="button"
                type="button"
                onClick={() => setPackId(pack.id)}
                aria-pressed={selected}
                sx={{
                  flex: 1,
                  p: 2.5,
                  borderRadius: 2,
                  border: "2px solid",
                  borderColor: selected ? "primary.main" : "border.default",
                  bgcolor: selected ? "accent.softer" : "surface.raised",
                  cursor: "pointer",
                  textAlign: "left",
                  font: "inherit",
                  color: "inherit",
                  position: "relative",
                  transition: (t) =>
                    `border-color ${t.motion.fast}, background-color ${t.motion.fast}, transform ${t.motion.fast}`,
                  "&:hover": { borderColor: "primary.light" },
                  "&:focus-visible": {
                    outline: "none",
                    borderColor: "primary.main",
                  },
                }}
              >
                {recommended && (
                  <Chip
                    label="Most popular"
                    size="small"
                    color="primary"
                    sx={{
                      position: "absolute",
                      top: -10,
                      left: 16,
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      letterSpacing: "0.04em",
                    }}
                  />
                )}
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.disabled",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontWeight: 600,
                  }}
                >
                  {pack.label}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mt: 0.5,
                  }}
                >
                  <BoltIcon sx={{ color: "primary.main" }} />
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, lineHeight: 1 }}
                  >
                    {formatCredits(pack.credits)}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", mt: 0.25, display: "block" }}
                >
                  credits
                </Typography>
                <Box
                  sx={{
                    mt: 2,
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {formatCents(pack.price_cents)}
                  </Typography>
                  {pack.bonus_pct > 0 && (
                    <Chip
                      label={`+${pack.bonus_pct}% bonus`}
                      size="small"
                      sx={{
                        bgcolor: "accent.soft",
                        color: "primary.light",
                        fontWeight: 600,
                      }}
                    />
                  )}
                </Box>
              </Box>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={startTopup.isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={startTopup.isPending}
        >
          {startTopup.isPending ? "Redirecting…" : "Continue to checkout"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
